import { useCallback, useMemo, useRef, useState } from 'react';
import { diffEdges, edgeMessage, type ChangeClass } from '@drawing2d/core';
import {
  basenameOf,
  describeCompareFailure,
  diffContentFor,
  runComparison,
  type Comparison,
  type CompareFailure,
} from '../compare.js';
import { downloadText } from '../pipeline.js';
import { t, ERROR_LABELS, type Lang } from '../i18n.js';
import { CompareStage, type CompareView } from './CompareStage.js';
import { DiffLegend } from './DiffLegend.js';
import { ChangeSummary, describeSheet } from './ChangeSummary.js';
import { ChangeList } from './ChangeList.js';
import { EntityDetail } from './EntityDetail.js';
import { DrawingBar } from './DrawingBar.js';

/**
 * The comparison surface.
 *
 * Two versions of one drawing, read in this browser, compared here, and shown as
 * one stage with three views over it. This module owns the state the views share
 * and nothing else: which sheet, which view, which classes are filtered out,
 * whether colour is in use, and which change is selected.
 *
 * The selection is an **index into `diff.entries`**, never an entity identifier.
 * Both versions contain an entity called "p0-1" and they are different entities,
 * so a selection keyed on the identifier would show the reviewer the wrong side
 * of the wrong change while looking entirely correct. The map back from a mark
 * on the before or after view is keyed by (which version, which page, which
 * identifier) for the same reason.
 *
 * The sheet selector iterates the *alignment*, not one document's pages. A
 * comparison sheet is a pair: after a sheet is inserted, base page 1 answers to
 * compare page 2, and a selector built from either document alone would put the
 * reviewer on the wrong sheet of the other version.
 */

const PAIRS = [
  { key: 'samplePairA' as const, before: 'pair-a.v1.pdf', after: 'pair-a.v2.pdf' },
  { key: 'samplePairSheets' as const, before: 'pair-sheets.v1.pdf', after: 'pair-sheets.v2.pdf' },
];

interface Side {
  bytes: Uint8Array;
  name: string;
}

export function Compare({ lang }: { lang: Lang }): JSX.Element {
  const [before, setBefore] = useState<Side | null>(null);
  const [after, setAfter] = useState<Side | null>(null);
  const [busy, setBusy] = useState(false);
  const [comparison, setComparison] = useState<Comparison | null>(null);
  const [failure, setFailure] = useState<CompareFailure | null>(null);

  const [view, setView] = useState<CompareView>('diff');
  const [sheetIndex, setSheetIndex] = useState(0);
  /**
   * `unchanged` starts hidden.
   *
   * It was the joint-largest class on the sample pair and it is drawn as a thin
   * continuous line over a drawing already made of thin continuous lines, so it
   * contributed a third of the marks and none of the information. It is one click
   * away in the legend, for a reviewer who wants to see what the matcher accounted
   * for rather than only what it flagged.
   */
  const [hidden, setHidden] = useState<ReadonlySet<ChangeClass>>(new Set(['unchanged']));
  const [monochrome, setMonochrome] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);

  const beforeInput = useRef<HTMLInputElement>(null);
  const afterInput = useRef<HTMLInputElement>(null);

  /**
   * The two sides, held where they can be read back the instant they are set.
   *
   * Reading a file is asynchronous, so two files chosen in quick succession
   * finish in either order and neither handler can rely on a render having
   * happened in between. Held only in state, the second file to arrive would see
   * a stale null for the first and the comparison would simply never start —
   * with both drop targets showing a loaded file, which is the worst kind of
   * quiet failure. The ref is written synchronously the moment a side is read,
   * and the run is triggered by whichever side finds the *other* already there,
   * so it happens exactly once however the two races resolve.
   */
  const sides = useRef<{ before: Side | null; after: Side | null }>({ before: null, after: null });

  const s = useCallback((k: Parameters<typeof t>[0]) => t(k, lang), [lang]);

  const run = useCallback(async (left: Uint8Array, right: Uint8Array) => {
    setBusy(true);
    setFailure(null);
    try {
      const result = await runComparison(left, right);
      setComparison(result);
      setSheetIndex(0);
      setSelected(null);
      setView('diff');
    } catch (err) {
      setFailure(describeCompareFailure(err));
      setComparison(null);
    } finally {
      setBusy(false);
    }
  }, []);

  const loadSide = useCallback(
    async (which: 'before' | 'after', file: File) => {
      const side: Side = { bytes: new Uint8Array(await file.arrayBuffer()), name: file.name };
      const other = which === 'before' ? sides.current.after : sides.current.before;
      sides.current = { ...sides.current, [which]: side };
      if (which === 'before') setBefore(side);
      else setAfter(side);
      if (!other) return;
      const { before: left, after: right } = sides.current;
      if (left && right) await run(left.bytes, right.bytes);
    },
    [run],
  );

  const loadPair = useCallback(
    async (pair: (typeof PAIRS)[number]) => {
      setBusy(true);
      setFailure(null);
      try {
        // Relative to the document, exactly as the conversion surface fetches
        // its samples. A request to any other origin would break the promise
        // that nothing leaves the machine, and the browser tests check it.
        const [left, right] = await Promise.all(
          [pair.before, pair.after].map(async (file) => {
            const response = await fetch(new URL(`fixtures/${file}`, document.baseURI));
            return new Uint8Array(await response.arrayBuffer());
          }),
        );
        if (!left || !right) throw new Error('sample pair could not be read');
        sides.current = {
          before: { bytes: left, name: pair.before },
          after: { bytes: right, name: pair.after },
        };
        setBefore(sides.current.before);
        setAfter(sides.current.after);
        await run(left, right);
      } catch (err) {
        setFailure(describeCompareFailure(err));
        setComparison(null);
        setBusy(false);
      }
    },
    [run],
  );

  const reset = useCallback(() => {
    sides.current = { before: null, after: null };
    setBefore(null);
    setAfter(null);
    setComparison(null);
    setFailure(null);
    setSelected(null);
    setHidden(new Set(['unchanged']));
    setMonochrome(false);
  }, []);

  const toggleClass = useCallback((changeClass: ChangeClass) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(changeClass)) next.delete(changeClass);
      else next.add(changeClass);
      return next;
    });
  }, []);

  /**
   * The map from a mark on the before or after view back to the comparison.
   * Keyed by the triple, because an identifier alone collides across versions.
   */
  const entryIndexBySide = useMemo(() => {
    const map = new Map<string, number>();
    for (const [index, entry] of (comparison?.diff.entries ?? []).entries()) {
      if (entry.base) map.set(`base|${entry.base.pageIndex}|${entry.base.entityId}`, index);
      if (entry.compare) map.set(`compare|${entry.compare.pageIndex}|${entry.compare.entityId}`, index);
    }
    return map;
  }, [comparison]);

  const entryOf = useCallback(
    (side: 'base' | 'compare', pageIndex: number, entityId: string): number | null =>
      entryIndexBySide.get(`${side}|${pageIndex}|${entityId}`) ?? null,
    [entryIndexBySide],
  );

  if (busy) return <p className="status">{s('comparing')}</p>;

  if (!comparison) {
    return (
      <section className="compare-picker">
        <p className="note">{s('compareIntro')}</p>
        {failure && <Failure failure={failure} lang={lang} onReset={reset} />}
        <div className="drops">
          <Dropzone
            which="before"
            title={s('dropBefore')}
            label={s('beforeVersion')}
            loaded={before?.name ?? null}
            loadedWord={s('loaded')}
            browse={s('browse')}
            inputRef={beforeInput}
            onFile={(file) => void loadSide('before', file)}
          />
          <Dropzone
            which="after"
            title={s('dropAfter')}
            label={s('afterVersion')}
            loaded={after?.name ?? null}
            loadedWord={s('loaded')}
            browse={s('browse')}
            inputRef={afterInput}
            onFile={(file) => void loadSide('after', file)}
          />
        </div>
        <p className="or">{s('samplePairs')}</p>
        <div className="samples">
          {PAIRS.map((pair) => (
            <button key={pair.key} data-pair={pair.key} onClick={() => void loadPair(pair)}>
              {s(pair.key)}
            </button>
          ))}
        </div>
        {(!before || !after) && <p className="note">{s('awaitingSides')}</p>}
      </section>
    );
  }

  const { diff, sheets, base, compare, identity, artefacts } = comparison;
  const sheet = sheets[Math.min(sheetIndex, sheets.length - 1)];
  const entry = selected === null ? undefined : diff.entries[selected];
  const edges = diffEdges(diff);

  return (
    <main className="result compare-result">
      <section className="viewer compare-viewer">
        <div className="edge-banners">
          {edges.map((edge) => (
            <p className="edge-banner" data-edge={edge} key={edge}>
              {edgeMessage(edge, diff, lang)}
            </p>
          ))}
        </div>

        <div className="viewer-bar">
          {/*
            Which two files these are. "Before" and "after" name the roles, and
            the surface knew both filenames the whole time — it stores them when
            the files are read — but showed neither, so two revisions open beside
            each other were told apart only by what was on them.
          */}
          <DrawingBar
            name={before && after ? `${before.name} → ${after.name}` : null}
            lang={lang}
            label="comparePairAgain"
            onLoadAnother={reset}
          />
          <div className="views" role="group" aria-label={s('legendTitle')}>
            {(['before', 'after', 'diff'] as const).map((v) => (
              <button
                key={v}
                data-view={v}
                className={view === v ? 'on' : ''}
                onClick={() => setView(v)}
              >
                {v === 'before' ? s('beforeVersion') : v === 'after' ? s('afterVersion') : s('diffVersion')}
              </button>
            ))}
          </div>
          {sheet && (
            <p className="sheet-caption" data-sheet={sheet.index}>
              {s('sheet')} {sheet.index + 1} ·{' '}
              {describeSheet(sheet.basePageIndex, sheet.comparePageIndex, lang)}
            </p>
          )}
          {sheets.length > 1 && (
            <div className="pages sheets">
              {sheets.map((candidate) => (
                <button
                  key={candidate.index}
                  data-sheet={candidate.index}
                  className={candidate.index === sheet?.index ? 'on' : ''}
                  onClick={() => {
                    setSheetIndex(candidate.index);
                    setSelected(null);
                  }}
                >
                  {s('sheet')} {candidate.index + 1}
                </button>
              ))}
            </div>
          )}
        </div>

        {sheet && (
          <>
            {/* Keyed by sheet alone. Keying by view as well would remount the
                stage on every toggle and quietly reset the zoom, which is the
                one thing the three views must survive together. */}
            <CompareStage
              key={sheet.index}
              sheet={sheet}
              diff={diff}
              view={view}
              lang={lang}
              hidden={hidden}
              monochrome={monochrome}
              baseImage={
                sheet.basePageIndex === null ? null : (base.pageImages[sheet.basePageIndex] ?? null)
              }
              compareImage={
                sheet.comparePageIndex === null
                  ? null
                  : (compare.pageImages[sheet.comparePageIndex] ?? null)
              }
              entryOf={entryOf}
              onSelect={setSelected}
              selected={selected}
            />
          </>
        )}
      </section>

      {/*
        The reviewer's question first, the instrument's readings last.

        This column used to open with the legend and give twenty rows of matcher
        profile, tolerance, coverage and sheet-alignment margin equal weight with
        everything else — the readings of the instrument, presented as the thing
        to read. The question a reviewer arrives with is "what changed", so that
        is now the first panel and it is a list they can step through. The rest is
        still all here, one fold down, because a measurement nobody can inspect is
        not a measurement.
      */}
      <aside className="panels">
        <ChangeList
          diff={diff}
          sheetIndex={sheet?.index ?? 0}
          lang={lang}
          selected={selected}
          onSelect={setSelected}
        />

        {entry && selected !== null && (
          <EntityDetail
            entry={entry}
            entryIndex={selected}
            diff={diff}
            base={base.document}
            compare={compare.document}
            lang={lang}
          />
        )}

        <details className="fold" data-fold="filters">
          <summary>{s('filtersTitle')}</summary>
          <DiffLegend
            diff={diff}
            lang={lang}
            hidden={hidden}
            onToggle={toggleClass}
            monochrome={monochrome}
            onMonochrome={setMonochrome}
          />
        </details>

        <details className="fold" data-fold="measurement">
          <summary>{s('measurementTitle')}</summary>
          <ChangeSummary diff={diff} identity={identity} lang={lang} />
        </details>

        <details className="fold" data-fold="files">
          <summary>
            {s('detailsFiles')}
            <span className="fold-count">{artefacts.manifests.length}</span>
          </summary>
          <ul className="artefacts diff-artefacts">
            {artefacts.manifests.map((m) => (
              <li key={m.storageKey} data-artefact={basenameOf(m.storageKey)}>
                <div>
                  <b>{basenameOf(m.storageKey)}</b>
                  <code>{m.sha256.slice(0, 16)}…</code>
                  <span className="bytes">{m.bytes.toLocaleString()} B</span>
                </div>
                <button
                  onClick={() =>
                    downloadText(
                      basenameOf(m.storageKey),
                      diffContentFor(comparison, m.storageKey),
                      m.contentType,
                    )
                  }
                >
                  {s('download')}
                </button>
              </li>
            ))}
          </ul>
          <p className="note">
            {s('compareElapsed')} {comparison.elapsedMs} ms
          </p>
        </details>

      </aside>
    </main>
  );
}

function Failure({
  failure,
  lang,
  onReset,
}: {
  failure: CompareFailure;
  lang: Lang;
  onReset: () => void;
}): JSX.Element {
  return (
    <section className="failure">
      <h2>{t('compareFailed', lang)}</h2>
      <p className="code">{failure.code}</p>
      <p>{(ERROR_LABELS[failure.code] ?? ERROR_LABELS['UNEXPECTED']!)[lang]}</p>
      <p className="detail">{failure.message}</p>
      <button onClick={onReset}>{t('reset', lang)}</button>
    </section>
  );
}

function Dropzone({
  which,
  title,
  label,
  loaded,
  loadedWord,
  browse,
  inputRef,
  onFile,
}: {
  which: 'before' | 'after';
  title: string;
  label: string;
  loaded: string | null;
  loadedWord: string;
  browse: string;
  inputRef: React.RefObject<HTMLInputElement>;
  onFile: (file: File) => void;
}): JSX.Element {
  return (
    <section
      className="dropzone compare-drop"
      data-drop={which}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) onFile(file);
      }}
    >
      <p className="drop-role">{label}</p>
      <p className="drop-title">{title}</p>
      <button className="primary" onClick={() => inputRef.current?.click()}>
        {browse}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        hidden
        data-side-input={which}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFile(file);
        }}
      />
      {loaded && (
        <p className="note drop-loaded">
          {loaded} — {loadedWord}
        </p>
      )}
    </section>
  );
}
